import React, { useEffect, useState } from "react";
import { api } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Shield, Check, X, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Admin() {
  const { user } = useApp();
  const { t } = useI18n();
  const [users, setUsers] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));

  useEffect(() => {
    load();
  }, []);

  const setAccess = async (u, status) => {
    try {
      await api.post(`/admin/users/${u.user_id}/access`, { status });
      toast.success(status === "approved" ? t("admin.approvedToast") : t("admin.revokedToast"));
      load();
    } catch (e) {
      toast.error("Action failed.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex items-center gap-3 mb-2">
        <span className="w-10 h-10 rounded-xl bg-[#1E429F] flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </span>
        <h1 className="font-serif text-3xl font-semibold text-[#0E382B]">{t("admin.title")}</h1>
      </div>
      <p className="text-[#52635B] mb-8">{t("admin.sub")}</p>

      {!users ? (
        <p className="text-[#52635B]">{t("admin.loading")}</p>
      ) : users.length === 0 ? (
        <p className="text-[#52635B]">{t("admin.noUsers")}</p>
      ) : (
        <div className="rounded-2xl border border-[#E2DFD7] overflow-hidden bg-white">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#E8F0EC] text-sm font-semibold text-[#0E382B]">
            <div className="col-span-3">{t("admin.name")}</div>
            <div className="col-span-4">{t("admin.email")}</div>
            <div className="col-span-2">{t("admin.role")}</div>
            <div className="col-span-3 text-right">{t("admin.status")}</div>
          </div>
          {users.map((u) => {
            const approved = u.is_admin || u.access_status === "approved";
            const isSelf = u.user_id === user?.user_id;
            return (
              <div
                key={u.user_id}
                data-testid={`admin-user-row-${u.user_id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-t border-[#F1EFEB] items-center"
              >
                <div className="md:col-span-3 font-medium text-[#16201B]">
                  {u.name || "—"} {isSelf && <span className="text-xs text-[#1E429F]">({t("admin.you")})</span>}
                </div>
                <div className="md:col-span-4 text-sm text-[#52635B] truncate">{u.email}</div>
                <div className="md:col-span-2 text-sm">
                  {u.is_admin ? (
                    <span className="inline-flex items-center gap-1 text-[#1E429F] font-medium"><Shield className="w-3.5 h-3.5" /> {t("admin.adminRole")}</span>
                  ) : (
                    <span className="text-[#52635B]">{t("admin.student")}</span>
                  )}
                </div>
                <div className="md:col-span-3 flex md:justify-end items-center gap-3">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${approved ? "bg-[#EAF5EF] text-[#0E5A35]" : "bg-[#FEF3C7] text-[#B45309]"}`}>
                    {approved ? t("admin.approved") : t("admin.pending")}
                  </span>
                  {!u.is_admin && (
                    approved ? (
                      <Button size="sm" variant="outline" onClick={() => setAccess(u, "pending")} data-testid={`admin-revoke-${u.user_id}`} className="rounded-full border-[#F8B4B4] text-[#9B1C1C] hover:bg-[#FDF2F2]">
                        <X className="w-4 h-4 mr-1" /> {t("admin.revoke")}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => setAccess(u, "approved")} data-testid={`admin-approve-${u.user_id}`} className="rounded-full bg-[#0E5A35] hover:bg-[#0b4a2c] text-white">
                        <Check className="w-4 h-4 mr-1" /> {t("admin.approve")}
                      </Button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
